import { LocalStorageKey } from "@/modules/local-storage/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";

const MEM_AGENT_COMPOSER_DRAFT_REQUEST_EVENT = "mem-agent-composer-draft-request";

interface MemAgentComposerDraftRequest {
  requestId: string;
  html: string;
}

/**
 * Unsent Mem Agent composer text, stored as the editor's HTML. localStorage is
 * shared by the main window and the standalone widget window (same origin), so
 * the draft survives panel unmounts, docked/floating handoffs, and widget
 * window reboots.
 */

/** True for documents with no text and no embedded media (e.g. `<p></p>`). */
export const isMemAgentComposerDraftEmpty = (html: string): boolean => {
  if (html.replace(/<[^>]*>/g, "").trim().length > 0) return false;
  return !/<(img|video|audio|iframe)\b/i.test(html);
};

export const writeMemAgentComposerDraft = (html: string): void => {
  try {
    localStorage.setItem(LocalStorageKey.MemAgentComposerDraft, html);
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[writeMemAgentComposerDraft] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

const isMemAgentComposerDraftRequest = (value: unknown): value is MemAgentComposerDraftRequest => {
  if (typeof value !== "object" || value === null) return false;
  const { requestId, html } = value as Partial<MemAgentComposerDraftRequest>;
  return typeof requestId === "string" && typeof html === "string";
};

const parseMemAgentComposerDraftRequest = (
  value: string | null
): MemAgentComposerDraftRequest | null => {
  if (value === null) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return isMemAgentComposerDraftRequest(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Persists a draft and asks any currently mounted composer to replace its
 * content. The custom event covers this window; the unique localStorage write
 * reaches the standalone desktop widget window through its `storage` event.
 */
export const requestMemAgentComposerDraft = (html: string): void => {
  const request: MemAgentComposerDraftRequest = {
    requestId: uuidModule.generate(),
    html,
  };
  writeMemAgentComposerDraft(html);

  try {
    localStorage.setItem(LocalStorageKey.MemAgentComposerDraftRequest, JSON.stringify(request));
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[requestMemAgentComposerDraft] Failed to notify other windows.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }

  window.dispatchEvent(
    new CustomEvent<MemAgentComposerDraftRequest>(MEM_AGENT_COMPOSER_DRAFT_REQUEST_EVENT, {
      detail: request,
    })
  );
};

export const subscribeMemAgentComposerDraftRequests = (
  handler: (html: string) => void
): (() => void) => {
  let lastHandledRequestId: string | null = null;

  const handleRequest = (request: MemAgentComposerDraftRequest | null) => {
    if (request === null || request.requestId === lastHandledRequestId) return;
    lastHandledRequestId = request.requestId;
    handler(request.html);
  };
  const handleSameWindowRequest = (event: Event) => {
    const request = (event as CustomEvent<unknown>).detail;
    handleRequest(isMemAgentComposerDraftRequest(request) ? request : null);
  };
  const handleOtherWindowRequest = (event: StorageEvent) => {
    if (event.key !== LocalStorageKey.MemAgentComposerDraftRequest) return;
    handleRequest(parseMemAgentComposerDraftRequest(event.newValue));
  };

  window.addEventListener(MEM_AGENT_COMPOSER_DRAFT_REQUEST_EVENT, handleSameWindowRequest);
  window.addEventListener("storage", handleOtherWindowRequest);
  return () => {
    window.removeEventListener(MEM_AGENT_COMPOSER_DRAFT_REQUEST_EVENT, handleSameWindowRequest);
    window.removeEventListener("storage", handleOtherWindowRequest);
  };
};

export const clearMemAgentComposerDraft = (): void => {
  try {
    localStorage.removeItem(LocalStorageKey.MemAgentComposerDraft);
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[clearMemAgentComposerDraft] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
  }
};

export const readMemAgentComposerDraft = (): string | null => {
  try {
    const value = localStorage.getItem(LocalStorageKey.MemAgentComposerDraft);
    if (value === null) return null;
    if (isMemAgentComposerDraftEmpty(value)) {
      clearMemAgentComposerDraft();
      return null;
    }
    return value;
  } catch (unknownErr) {
    const err = unknownErr as Error;

    logger.warn({
      message: "[readMemAgentComposerDraft] Failed.",
      info: {
        err: objectModule.safeErrorAsJson(err),
      },
    });
    return null;
  }
};
